import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../../lib/api';
import { isValidCustomerPhone } from '../../lib/domain';
import { Badge, ErrorState, Field, Icon, PageHeader, Spinner } from '../../components/UI';
import { Link } from '../../lib/router';

const date = (value, time = false) =>
  value
    ? new Intl.DateTimeFormat('en-SG', {
        dateStyle: 'medium',
        ...(time ? { timeStyle: 'short' } : {}),
      }).format(new Date(value))
    : '—';

const money = (cents) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(
    (cents || 0) / 100,
  );

const discountLabel = (voucher) => voucher.discountType === 'amount'
  ? money(voucher.discountAmountCents)
  : `${voucher.discountPercent}%`;

function useLoad(loader, deps = []) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = () => {
    setState({ loading: true, data: null, error: null });
    let live = true;
    loader()
      .then((data) => live && setState({ loading: false, data, error: null }))
      .catch((error) => live && setState({ loading: false, data: null, error }));
    return () => {
      live = false;
    };
  };
  useEffect(load, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [state, load];
}

function Async({ state, retry, children, label = 'Loading page' }) {
  if (state.loading) return <Spinner label={label} />;
  if (state.error) return <ErrorState error={state.error} retry={retry} />;
  return children(state.data);
}

export function DashboardPage() {
  const [state, retry] = useLoad(api.summary);
  return (
    <Async state={state} retry={retry}>
      {(data) => (
        <>
          <PageHeader
            title="Overview"
            description="Your business activity at a glance."
          />
          <section className="metric-grid">
            <article className="metric-customers">
              <span>Registered customers</span>
              <strong>{data.customers}</strong>
              <small>Unique phone numbers</small>
            </article>
          </section>
          <section className="dashboard-grid dashboard-overview-grid">
            <section className="overview-voucher">
              <div className="panel-head">
                <div>
                  <h2>Current voucher</h2>
                  <p>Available for customers to claim now.</p>
                </div>
              </div>
              {data.offer?.active ? (
                <VoucherCard
                  voucher={data.offer}
                  status="active"
                  action={<Link className="button secondary compact" to="/merchant/voucher">Manage voucher</Link>}
                />
              ) : (
                <div className="empty panel">No ongoing voucher.</div>
              )}
            </section>
            <article className="panel activity">
              <div className="panel-head">
                <div>
                  <h2>Recent activity</h2>
                  <p>Your customers’ latest activity.</p>
                </div>
              </div>
              <VoucherRows items={data.recentClaims} />
            </article>
          </section>
        </>
      )}
    </Async>
  );
}

function VoucherCard({ voucher, status, action, note, subdued = false }) {
  return (
    <div className={`loyal-voucher-card ${subdued ? 'is-subdued' : ''}`}>
      <div className="loyal-voucher-value" aria-label={`${discountLabel(voucher)} off`}>
        <strong>{discountLabel(voucher)}</strong>
        <span>OFF</span>
      </div>
      <div className="loyal-voucher-body">
        <div className="loyal-voucher-heading">
          <strong>{voucher.title}</strong>
          {status && <Badge status={status} />}
        </div>
        <p>{voucher.description || 'A voucher for the customer’s next visit.'}</p>
        <div className="loyal-voucher-terms">
          {voucher.minimumSpendCents > 0 && <span>Minimum spend {money(voucher.minimumSpendCents)}</span>}
          {voucher.validityDays > 0 && <span>Valid for {voucher.validityDays} days</span>}
        </div>
        <div className="loyal-voucher-code">
          <span>Voucher code</span>
          <code>{voucher.code}</code>
        </div>
        {status === 'pending' && (
          <small className="voucher-redemption-warning">Cannot be redeemed yet.</small>
        )}
        {note && <small className="loyal-voucher-note">{note}</small>}
      </div>
      {action && <div className="loyal-voucher-action">{action}</div>}
    </div>
  );
}

export function AnalyticsPage() {
  return (
    <>
      <PageHeader
        title="Analytics"
        description="Track voucher performance and customer returns."
      />
      <section className="analytics-locked" aria-labelledby="analytics-title">
        <div className="analytics-lock-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="5" y="10" width="14" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
        </div>
        <div>
          <h2 id="analytics-title">Advanced analytics is locked</h2>
          <p>
            Upgrade access will include voucher performance, return patterns, and
            date-range reporting.
          </p>
          <ul>
            <li>Claims and redemptions over time</li>
            <li>Voucher-by-voucher performance</li>
            <li>Customer return trends</li>
          </ul>
          <button className="button upgrade-button" disabled>
            Not included in LoyalQR Basic
          </button>
        </div>
      </section>
    </>
  );
}

export function MerchantRelationsPage() {
  return (
    <>
      <PageHeader
        title="Merchant Network"
        description="Collaborate with other businesses to reach more customers."
      />
      <section className="relations-preview" aria-labelledby="merchant-relations-title">
        <header className="relations-preview-head">
          <div className="relations-preview-mark" aria-hidden="true">
            <Icon name="network" />
          </div>
          <div>
            <Badge status="locked" />
            <h2 id="merchant-relations-title">Grow Through Merchant Partnerships</h2>
            <p>Find partners, launch shared voucher campaigns, and get discovered across our merchant network.</p>
          </div>
        </header>
        <ul className="relations-feature-list">
          <li className="is-featured">
            <img className="relations-feature-image" src="/merchant-relations-local-discovery.jpg" alt="" />
            <div>
              <strong>Be Discovered Locally</strong>
              <span>Put your business in front of customers across our merchant network.</span>
            </div>
            <Badge status="locked" />
          </li>
          <li className="is-featured">
            <img className="relations-feature-image" src="/merchant-relations-shared-campaigns.jpg" alt="" />
            <div>
              <strong>Shared Voucher Campaigns</strong>
              <span>Plan offers that customers can use across participating businesses.</span>
            </div>
            <Badge status="locked" />
          </li>
          <li>
            <img className="relations-feature-image" src="/merchant-relations-partner-discovery.jpg" alt="" />
            <div>
              <strong>Partner Discovery</strong>
              <span>Find complementary businesses with similar customer communities.</span>
            </div>
            <Badge status="locked" />
          </li>
        </ul>
        <p className="relations-preview-note">These tools are not included in LoyalQR Basic.</p>
      </section>
    </>
  );
}

export function StaffAccessPage() {
  return (
    <>
      <PageHeader
        title="Staff Access"
        description="Control staff access and permissions."
      />
      <section className="access-locked" aria-labelledby="staff-access-title">
        <div className="access-lock-mark" aria-hidden="true">
          <Icon name="people" />
        </div>
        <div>
          <Badge status="locked" />
          <h2 id="staff-access-title">Staff accounts are locked</h2>
          <p>Set up staff accounts with the right access for each team member.</p>
        </div>
      </section>
    </>
  );
}

export function PlanBillingPage() {
  return (
    <>
      <PageHeader
        title="Plan & Billing"
        description="View your current plan and billing details."
      />
      <section className="billing-summary" aria-labelledby="plan-title">
        <div className="billing-summary-head">
          <div>
            <span>Current plan</span>
            <h2 id="plan-title">LoyalQR Basic</h2>
          </div>
          <Badge status="free" />
        </div>
        <dl>
          <div>
            <dt>Billing</dt>
            <dd>No payment method required</dd>
          </div>
          <div>
            <dt>Next payment</dt>
            <dd>None scheduled</dd>
          </div>
        </dl>
        <p className="billing-note">
          No payment is required for your current plan.
        </p>
      </section>
    </>
  );
}

function VoucherRows({ items, onSelect }) {
  if (!items?.length) return <div className="empty">No vouchers to show.</div>;
  return (
    <div className="table-wrap voucher-table" role="region" aria-label="Vouchers table" tabIndex="0">
      <p className="table-scroll-hint">Swipe sideways to see all columns</p>
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Customer</th>
            <th>Claimed</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.voucher.id}>
              <td>
                <code>{item.voucher.code}</code>
              </td>
              <td>{item.customer.phone}</td>
              <td>{date(item.voucher.claimedAt)}</td>
              <td>
                <Badge status={item.voucher.status} />
              </td>
              <td>
                {onSelect && (
                  <button className="text-button" onClick={() => onSelect(item)}>
                    Select
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VoucherSettingsPage() {
  const [vouchersState, reloadVouchers] = useLoad(api.voucherDefinitions);
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState('');
  const [stoppingID, setStoppingID] = useState('');

  function createVoucher() {
    setNotice('');
    setEditor({ mode: 'create', voucher: null });
  }

  function editVoucher(voucher) {
    setNotice('');
    setEditor({ mode: 'edit', voucher });
  }

  function savedVoucher(message) {
    setEditor(null);
    setNotice(message);
    reloadVouchers();
  }

  async function stopVoucher(voucher) {
    if (!window.confirm(`Stop “${voucher.title}”? Customers will no longer be able to claim it.`)) return;
    setStoppingID(voucher.id);
    setNotice('');
    try {
      await api.saveOffer({
        id: voucher.id,
        code: voucher.code,
        title: voucher.title,
        description: voucher.description,
        discountPercent: voucher.discountPercent,
        discountType: voucher.discountType,
        discountAmountCents: voucher.discountAmountCents,
        minimumSpendCents: voucher.minimumSpendCents,
        validityDays: voucher.validityDays,
        terms: voucher.terms,
        active: false,
      });
      setNotice('Voucher stopped. It is now listed under Past vouchers.');
      reloadVouchers();
    } catch (error) {
      setNotice(error.message);
    } finally {
      setStoppingID('');
    }
  }

  return (
    <>
      <PageHeader
        title="Vouchers"
        description="Manage ongoing vouchers and review past ones."
        action={
          !editor && (
            <button className="button primary" onClick={createVoucher}>
              Create new voucher
            </button>
          )
        }
      />
      {notice && <div className="alert success page-notice">{notice}</div>}
      {editor && (
        <section className="voucher-editor">
          <OfferForm
            key={editor.voucher?.id || 'new'}
            offer={editor.voucher}
            mode={editor.mode}
            hasOngoingVoucher={Boolean(vouchersState.data?.some((item) => item.active))}
            onCancel={() => setEditor(null)}
            onSaved={savedVoucher}
          />
        </section>
      )}
      <Async state={vouchersState} retry={reloadVouchers} label="Loading vouchers">
        {(items) => <VoucherDefinitionLists items={items} onEdit={editVoucher} onCreate={createVoucher} onStop={stopVoucher} stoppingID={stoppingID} />}
      </Async>
    </>
  );
}

function VoucherDefinitionLists({ items, onEdit, onCreate, onStop, stoppingID }) {
  const ongoing = items.filter((item) => item.active);
  const past = items.filter((item) => !item.active);

  const list = (vouchers, editable) => (
    <div className="voucher-definition-list">
      {vouchers.map((voucher) => (
        <VoucherCard
          key={voucher.id}
          voucher={voucher}
          status={voucher.active ? 'active' : 'past'}
          subdued={!voucher.active}
          note={`Created ${date(voucher.createdAt)}`}
          action={editable ? (
            <div className="voucher-card-actions">
              <button className="button secondary compact" onClick={() => onEdit(voucher)}>Edit voucher</button>
              <button className="button stop-voucher compact" onClick={() => onStop(voucher)} disabled={stoppingID === voucher.id}>
                {stoppingID === voucher.id ? 'Stopping…' : 'Stop voucher'}
              </button>
            </div>
          ) : null}
        />
      ))}
    </div>
  );

  return (
    <div className="voucher-catalog">
      <section className="voucher-section ongoing-vouchers">
        <div className="section-heading">
          <div>
            <h2>Ongoing vouchers</h2>
            <p>Available for customers to claim now.</p>
          </div>
        </div>
        {ongoing.length ? (
          list(ongoing, true)
        ) : (
          <div className="empty voucher-empty">
            <strong>No ongoing voucher</strong>
            <p>Create one when you are ready to invite customers back.</p>
            <button className="button primary" onClick={onCreate}>
              Create new voucher
            </button>
          </div>
        )}
      </section>
      <section className="voucher-section past-vouchers">
        <div className="section-heading">
          <div>
            <h2>Past vouchers</h2>
            <p>No longer available for new claims.</p>
          </div>
        </div>
        {past.length ? list(past, false) : <div className="empty">No past vouchers yet.</div>}
      </section>
    </div>
  );
}

const blankVoucher = {
  id: '',
  code: '',
  title: '',
  description: '',
  discountType: 'percentage',
  discountPercent: 10,
  discountAmountCents: 0,
  minimumSpendCents: 0,
  validityDays: 0,
  terms: '',
  active: true,
};

function OfferForm({ offer, mode, hasOngoingVoucher, onSaved, onCancel }) {
  const source = offer || blankVoucher;
  const [form, setForm] = useState({
    ...source,
    discountAmount: source.discountAmountCents ? (source.discountAmountCents / 100).toFixed(2) : '',
    minimumSpend: source.minimumSpendCents ? (source.minimumSpendCents / 100).toFixed(2) : '',
    validityDays: source.validityDays || '',
  });
  const [busy, setBusy] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [notice, setNotice] = useState('');
  const [errors, setErrors] = useState({});
  const update = (key) => (event) =>
    setForm({
      ...form,
      [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    });

  async function generateCode() {
    setGeneratingCode(true);
    setNotice('');
    setErrors({});
    try {
      const result = await api.generateVoucherCode();
      setForm((current) => ({ ...current, code: result.code }));
    } catch (error) {
      setNotice(error.message);
    } finally {
      setGeneratingCode(false);
    }
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    setErrors({});
    const payload = {
      id: form.id,
      code: form.code,
      title: form.title,
      description: form.description,
      discountType: form.discountType,
      discountPercent: Number(form.discountPercent),
      discountAmountCents: form.discountAmount === '' ? 0 : Math.round(Number(form.discountAmount) * 100),
      minimumSpendCents: form.minimumSpend === '' ? 0 : Math.round(Number(form.minimumSpend) * 100),
      validityDays: form.validityDays === '' ? 0 : Number(form.validityDays),
      terms: form.terms,
      active: form.active,
    };
    try {
      if (mode === 'create' && hasOngoingVoucher && !window.confirm('Creating this voucher will stop the current ongoing voucher. Continue?')) {
        setBusy(false);
        return;
      }
      await (mode === 'create' ? api.newCampaign(payload) : api.saveOffer(payload));
      onSaved(mode === 'create' ? 'New voucher created and available to claim.' : 'Voucher changes saved.');
    } catch (error) {
      setErrors(error.fields || {});
      setNotice(error.message);
    } finally {
      setBusy(false);
    }
  }

  const previewVoucher = {
    ...form,
    code: String(form.code || '').trim() || 'AUTO-GENERATED',
    title: String(form.title || '').trim() || 'Your voucher headline',
    description: String(form.description || '').trim() || 'Your voucher description will appear here.',
    discountPercent: Number(form.discountPercent) || 0,
    discountAmountCents: Math.round((Number(form.discountAmount) || 0) * 100),
    minimumSpendCents: Math.round((Number(form.minimumSpend) || 0) * 100),
    validityDays: form.validityDays === '' ? 0 : Number(form.validityDays),
  };

  return (
      <form className="panel form-panel voucher-form" onSubmit={save}>
        <div className="panel-head">
          <div>
            <h2>{mode === 'create' ? 'Create new voucher' : 'Edit ongoing voucher'}</h2>
            <p>{mode === 'create' ? 'The new voucher becomes available as soon as it is created.' : 'Changes apply to future claims.'}</p>
          </div>
        </div>
        {notice && (
          <div className={Object.keys(errors).length ? 'alert error' : 'alert success'}>
            {notice}
          </div>
        )}
        <div className="form-grid">
          <Field className="full-field" label="Voucher headline" error={errors.title}>
            <input value={form.title} onChange={update('title')} maxLength="120" placeholder="e.g. 10% off your next visit" required />
          </Field>
          <Field
            className="full-field"
            label="Voucher code"
            hint="One unique code for this voucher. Leave blank to generate one automatically."
            error={errors.code}
          >
            <div className="code-field-control">
              <input
                className="voucher-code-input"
                value={form.code || ''}
                onChange={update('code')}
                placeholder="e.g. VIP-2026"
                maxLength="24"
                autoComplete="off"
              />
              <button
                type="button"
                className="button secondary"
                onClick={generateCode}
                disabled={busy || generatingCode}
              >
                {generatingCode ? 'Generating…' : 'Generate code'}
              </button>
            </div>
          </Field>
          <Field label="Discount type" error={errors.discountType}>
            <select value={form.discountType} onChange={update('discountType')}>
              <option value="percentage">Percentage (%)</option>
              <option value="amount">Fixed amount (S$)</option>
            </select>
          </Field>
          {form.discountType === 'amount' ? (
            <Field label="Discount amount (S$)" error={errors.discountAmountCents}>
              <input type="number" min="0.01" step=".01" value={form.discountAmount} onChange={update('discountAmount')} required />
            </Field>
          ) : (
            <Field label="Discount percentage (%)" error={errors.discountPercent}>
              <input type="number" min="1" max="100" value={form.discountPercent} onChange={update('discountPercent')} required />
            </Field>
          )}
          <Field className="full-field" label="Description" error={errors.description}>
            <textarea value={form.description} onChange={update('description')} rows="3" placeholder="e.g. A little thank-you for visiting us." />
          </Field>
          <section className="voucher-live-preview full-field" aria-labelledby="voucher-preview-title">
            <div className="voucher-live-preview-heading">
              <div>
                <h3 id="voucher-preview-title">Customer preview</h3>
                <p>This updates as you fill in the voucher.</p>
              </div>
              <span>Preview</span>
            </div>
            <VoucherCard
              voucher={previewVoucher}
              note={String(form.terms || '').trim() || undefined}
            />
          </section>
          <details className="voucher-optional-details full-field">
            <summary>Optional voucher details</summary>
            <div className="optional-details-grid">
              <Field label="Minimum spend (S$)" error={errors.minimumSpendCents}>
                <input type="number" min="0" step=".01" value={form.minimumSpend} onChange={update('minimumSpend')} placeholder="No minimum spend" />
              </Field>
              <Field label="Validity (days)" error={errors.validityDays}>
                <input type="number" min="1" max="365" value={form.validityDays} onChange={update('validityDays')} placeholder="No expiry" />
              </Field>
              <Field className="full-field" label="Terms and conditions" error={errors.terms}>
                <textarea value={form.terms} onChange={update('terms')} rows="3" placeholder="Example: One voucher per visit. Cannot be combined with other promotions." />
              </Field>
            </div>
          </details>
        </div>
        {mode === 'edit' && <div className="form-note">Changes apply to future claims. Existing claims keep their original terms.</div>}
        <div className="button-row voucher-form-actions">
          <button className="button primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create voucher' : 'Save changes'}
          </button>
          {mode === 'create' && <button
            className="button secondary"
            type="button"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>}
        </div>
      </form>
  );
}

function CustomerRows({ items, selectedID, onSelect }) {
  if (!items.length) return <div className="empty">No customers match this search.</div>;
  return (
    <div className="table-wrap customer-table" role="region" aria-label="Customers table" tabIndex="0">
      <p className="table-scroll-hint">Swipe sideways to see all columns</p>
      <table>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Registered</th>
            <th>Claims</th>
            <th>Redemptions</th>
            <th>Last redemption</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className={`customer-row ${selectedID === item.id ? 'selected-row' : ''}`}
              onClick={() => onSelect(item)}
              onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onSelect(item);
                }
              }}
              tabIndex="0"
              aria-selected={selectedID === item.id}
            >
              <td>
                <span className="customer-cell">
                  {(item.firstName || item.lastName) && <strong>{[item.firstName, item.lastName].filter(Boolean).join(' ')}</strong>}
                  <span>{item.phone}</span>
                </span>
              </td>
              <td>{date(item.registeredAt)}</td>
              <td>{item.claims}</td>
              <td>{item.redemptions}</td>
              <td>{date(item.lastRedemptionAt)}</td>
              <td>
                <button className="button secondary compact view-vouchers" onClick={(event) => {
                  event.stopPropagation();
                  onSelect(item);
                }}>
                  View vouchers
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RedemptionConfirmation({ customer, item, busy, onCancel, onConfirm }) {
  return (
    <section className="redemption-confirmation" aria-labelledby={`redeem-${item.voucher.id}`}>
      <div>
        <h4 id={`redeem-${item.voucher.id}`}>Confirm this redemption</h4>
        <p>This action cannot be undone. Check the customer and voucher before continuing.</p>
      </div>
      <dl>
        <div><dt>Customer</dt><dd>{[customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.phone}</dd></div>
        <div><dt>Mobile</dt><dd>{customer.phone}</dd></div>
        <div><dt>Voucher</dt><dd>{item.voucher.title}</dd></div>
        <div><dt>Code</dt><dd><code>{item.voucher.code}</code></dd></div>
        <div><dt>Value</dt><dd>{discountLabel(item.voucher)} off</dd></div>
        <div><dt>Expiry</dt><dd>{date(item.voucher.expiresAt)}</dd></div>
      </dl>
      {(item.voucher.minimumSpendCents > 0 || item.voucher.terms) && (
        <p className="redemption-confirmation-terms">
          {item.voucher.minimumSpendCents > 0 && <>Minimum spend {money(item.voucher.minimumSpendCents)}. </>}
          {item.voucher.terms}
        </p>
      )}
      <div className="button-row redemption-confirmation-actions">
        <button className="button danger" type="button" onClick={() => onConfirm(item)} disabled={busy}>
          {busy ? 'Redeeming…' : 'Confirm redemption'}
        </button>
        <button className="button secondary" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </section>
  );
}

function CustomerVoucherHistory({ customer, state, activeVoucher, onRedeemed, onClaimed }) {
  const [redeemingID, setRedeemingID] = useState('');
  const [confirmingItem, setConfirmingItem] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setMessage(null);
    setConfirmingItem(null);
  }, [customer?.id]);

  if (!customer) {
    return (
      <div className="empty">
        <strong>Select a customer</strong>
        <p>View their claimed and redeemed vouchers here.</p>
      </div>
    );
  }
  if (state.loading) return <Spinner label="Loading customer vouchers" />;
  if (state.error) return <ErrorState error={state.error} />;

  const claimed = state.items.filter((item) => ['ready', 'pending'].includes(item.voucher.status));
  const redeemed = state.items.filter((item) => item.voucher.status === 'redeemed');
  const expired = state.items.filter((item) => item.voucher.status === 'expired');
  const hasCurrentVoucher = state.items.some((item) => item.campaign?.active);

  async function claimCurrentVoucher() {
    const customerLabel = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.phone;
    if (!window.confirm(`Claim “${activeVoucher.title}” for ${customerLabel}?`)) return;
    setClaimBusy(true);
    setMessage(null);
    try {
      const country = customer.phone.startsWith('+60') ? 'MY' : 'SG';
      const claimedVoucher = await api.staffClaim({ country, phone: customer.phone });
      onClaimed(claimedVoucher);
      setMessage({ tone: 'success', text: 'Current voucher claimed for this customer.' });
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setClaimBusy(false);
    }
  }

  async function redeemVoucher(item) {
    setRedeemingID(item.voucher.id);
    setMessage(null);
    try {
      const updated = await api.redeem(item.voucher.id);
      onRedeemed(updated);
      setMessage({ tone: 'success', text: 'Voucher redeemed successfully.' });
      setConfirmingItem(null);
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setRedeemingID('');
    }
  }

  const group = (title, items, allowRedeem = false) => (
    <section className="customer-voucher-group">
      <h3>{title}</h3>
      {items.length ? (
        <div className="customer-voucher-list">
          {items.map((item) => (
            <div key={item.voucher.id} className={`customer-voucher-item ${confirmingItem?.voucher.id === item.voucher.id ? 'redeeming' : ''}`}>
              <VoucherCard
                voucher={item.voucher}
                status={item.voucher.status}
                subdued={['redeemed', 'expired'].includes(item.voucher.status)}
                note={
                  item.voucher.status === 'redeemed'
                    ? `Redeemed ${date(item.voucher.redeemedAt, true)}`
                    : item.voucher.status === 'pending'
                      ? `Claimed ${date(item.voucher.claimedAt)} · Redeem from ${date(item.voucher.eligibleAt)}`
                      : `Claimed ${date(item.voucher.claimedAt)}`
                }
                action={allowRedeem && item.voucher.status === 'ready' ? (
                  <button className="button primary compact" onClick={() => setConfirmingItem(item)} disabled={Boolean(redeemingID)}>
                    Review redemption
                  </button>
                ) : null}
              />
              {confirmingItem?.voucher.id === item.voucher.id && (
                <RedemptionConfirmation
                  customer={customer}
                  item={item}
                  busy={redeemingID === item.voucher.id}
                  onCancel={() => setConfirmingItem(null)}
                  onConfirm={redeemVoucher}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="customer-voucher-empty">None</p>
      )}
    </section>
  );

  return (
    <>
      <div className="panel-head customer-history-head">
        <div>
          <h2>{[customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.phone}</h2>
          {(customer.firstName || customer.lastName) && <p>{customer.phone}</p>}
        </div>
      </div>
      {message && (
        <div className={`alert ${message.tone === 'error' ? 'error' : 'success'} customer-redeem-notice`}>
          {message.text}
        </div>
      )}
      {!hasCurrentVoucher && activeVoucher?.active && (
        <section className="customer-voucher-group available-voucher-group">
          <h3>Available to claim</h3>
          <VoucherCard
            voucher={activeVoucher}
            status="unclaimed"
            note="This customer has not claimed the ongoing voucher."
            action={(
              <button className="button primary compact" onClick={claimCurrentVoucher} disabled={claimBusy}>
                {claimBusy ? 'Claiming…' : 'Claim for customer'}
              </button>
            )}
          />
        </section>
      )}
      {group('Claimed vouchers', claimed, true)}
      {group('Redeemed vouchers', redeemed)}
      {expired.length > 0 && group('Expired vouchers', expired)}
    </>
  );
}

export function RedeemPage() {
  const [search, setSearch] = useState('');
  const [searchState, setSearchState] = useState({ status: 'idle', items: [], error: null });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [voucherState, setVoucherState] = useState({ loading: false, items: [], error: null });
  const [confirmingItem, setConfirmingItem] = useState(null);
  const [redeemingID, setRedeemingID] = useState('');
  const [notice, setNotice] = useState('');

  function readableError(error) {
    return error?.name === 'TypeError' || /failed to fetch|network/i.test(error?.message || '')
      ? 'We can’t connect to LoyalQR right now. Check your connection and try again.'
      : error?.message || 'Something went wrong. Please try again.';
  }

  async function findCustomer(event) {
    event.preventDefault();
    const query = search.trim();
    if (query.length < 3) {
      setSearchState({ status: 'error', items: [], error: new Error('Enter at least 3 characters or digits.') });
      return;
    }
    setSearchState({ status: 'loading', items: [], error: null });
    setSelectedCustomer(null);
    setConfirmingItem(null);
    setNotice('');
    try {
      const result = await api.customers({ search: query, page: 1, pageSize: 20 });
      setSearchState({ status: 'success', items: result.items, error: null });
      if (result.items.length === 1) await selectCustomer(result.items[0]);
    } catch (error) {
      setSearchState({ status: 'error', items: [], error: new Error(readableError(error)) });
    }
  }

  async function selectCustomer(customer) {
    setSelectedCustomer(customer);
    setVoucherState({ loading: true, items: [], error: null });
    setConfirmingItem(null);
    setNotice('');
    try {
      const items = await api.customerVouchers(customer.id);
      setVoucherState({ loading: false, items, error: null });
    } catch (error) {
      setVoucherState({ loading: false, items: [], error });
    }
  }

  async function redeemVoucher(item) {
    setRedeemingID(item.voucher.id);
    setNotice('');
    try {
      const updated = await api.redeem(item.voucher.id);
      setVoucherState((current) => ({
        ...current,
        items: current.items.map((entry) => entry.voucher.id === updated.voucher.id ? updated : entry),
      }));
      setConfirmingItem(null);
      setNotice(`${item.voucher.title} was redeemed for ${[selectedCustomer.firstName, selectedCustomer.lastName].filter(Boolean).join(' ') || selectedCustomer.phone}.`);
    } catch (error) {
      setNotice(readableError(error));
    } finally {
      setRedeemingID('');
    }
  }

  const ready = voucherState.items.filter((item) => item.voucher.status === 'ready');
  const waiting = voucherState.items.filter((item) => item.voucher.status === 'pending');

  return (
    <>
      <PageHeader title="Redeem a voucher" description="Find the customer, check the voucher, and confirm redemption." />
      <div className="redeem-workspace">
        <section className="panel redeem-search-panel">
          <div className="panel-head">
            <div>
              <h2>Find customer</h2>
              <p>Search using the mobile number they used to claim.</p>
            </div>
          </div>
          <form className="redeem-search-form" onSubmit={findCustomer}>
            <Field label="Mobile number or customer name">
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  if (searchState.status === 'error') setSearchState({ status: 'idle', items: [], error: null });
                }}
                autoComplete="off"
                placeholder="e.g. 9123 4567"
              />
            </Field>
            <button className="button primary" disabled={searchState.status === 'loading'}>
              {searchState.status === 'loading' ? 'Searching…' : 'Find customer'}
            </button>
          </form>
          {searchState.status === 'error' && <div className="alert error" role="alert">{searchState.error?.message}</div>}
          {searchState.status === 'success' && !searchState.items.length && (
            <div className="redeem-empty"><strong>No matching customer</strong><p>Check the number and try again.</p></div>
          )}
          {searchState.items.length > 0 && (
            <div className="redeem-customer-results" aria-label="Matching customers">
              {searchState.items.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  className={selectedCustomer?.id === customer.id ? 'is-selected' : ''}
                  onClick={() => selectCustomer(customer)}
                >
                  <span><strong>{[customer.firstName, customer.lastName].filter(Boolean).join(' ') || 'Customer'}</strong><small>{customer.phone}</small></span>
                  <span>View vouchers</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="panel redeem-voucher-panel" aria-live="polite">
          {!selectedCustomer && (
            <div className="redeem-empty"><strong>Start with a mobile number</strong><p>The customer’s vouchers will appear here.</p></div>
          )}
          {selectedCustomer && voucherState.loading && <Spinner label="Checking vouchers" />}
          {selectedCustomer && voucherState.error && <ErrorState error={voucherState.error} retry={() => selectCustomer(selectedCustomer)} />}
          {selectedCustomer && !voucherState.loading && !voucherState.error && (
            <>
              <div className="panel-head redeem-customer-heading">
                <div><h2>{[selectedCustomer.firstName, selectedCustomer.lastName].filter(Boolean).join(' ') || selectedCustomer.phone}</h2>{(selectedCustomer.firstName || selectedCustomer.lastName) && <p>{selectedCustomer.phone}</p>}</div>
                {ready.length > 0 && <Badge status="ready" />}
                {!ready.length && waiting.length > 0 && <Badge status="pending" />}
              </div>
              {notice && <div className={`alert ${/was redeemed/.test(notice) ? 'success' : 'error'}`} role={/was redeemed/.test(notice) ? 'status' : 'alert'}>{notice}</div>}
              {ready.length ? (
                <div className="redeem-voucher-list">
                  {ready.map((item) => (
                    <div key={item.voucher.id} className={`customer-voucher-item ${confirmingItem?.voucher.id === item.voucher.id ? 'redeeming' : ''}`}>
                      <VoucherCard
                        voucher={item.voucher}
                        status="ready"
                        note={`Claimed ${date(item.voucher.claimedAt)} · Expires ${date(item.voucher.expiresAt)}`}
                        action={<button className="button primary compact" type="button" onClick={() => setConfirmingItem(item)}>Review redemption</button>}
                      />
                      {confirmingItem?.voucher.id === item.voucher.id && (
                        <RedemptionConfirmation customer={selectedCustomer} item={item} busy={redeemingID === item.voucher.id} onCancel={() => setConfirmingItem(null)} onConfirm={redeemVoucher} />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="redeem-empty"><strong>No voucher ready to redeem</strong><p>{waiting.length ? 'A claimed voucher is still waiting for its redemption date.' : 'This customer has no active voucher.'}</p></div>
              )}
              {waiting.length > 0 && (
                <details className="waiting-vouchers"><summary>{waiting.length} voucher{waiting.length === 1 ? '' : 's'} not ready yet</summary><div>{waiting.map((item) => <VoucherCard key={item.voucher.id} voucher={item.voucher} status="pending" note={`Redeem from ${date(item.voucher.eligibleAt)}`} subdued />)}</div></details>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}

export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [newCustomer, setNewCustomer] = useState({ country: 'SG', phone: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');
  const [addMessage, setAddMessage] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeVoucher, setActiveVoucher] = useState(null);
  const [voucherState, setVoucherState] = useState({ loading: false, items: [], error: null });
  const loader = () => api.customers({ search, page, pageSize: 20 });
  const [state, retry] = useLoad(loader, [search, page]);

  useEffect(() => {
    let live = true;
    api.offer().then((voucher) => {
      if (live) setActiveVoucher(voucher?.active ? voucher : null);
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  async function viewCustomer(customer) {
    setSelectedCustomer(customer);
    setVoucherState({ loading: true, items: [], error: null });
    try {
      const items = await api.customerVouchers(customer.id);
      setVoucherState({ loading: false, items, error: null });
    } catch (error) {
      setVoucherState({ loading: false, items: [], error });
    }
  }

  async function addCustomer(event) {
    event.preventDefault();
    setAddError('');
    setAddMessage('');
    if (!isValidCustomerPhone(newCustomer.phone, newCustomer.country)) {
      setAddError('Enter a valid Singapore or Malaysia mobile number.');
      return;
    }
    setAddBusy(true);
    try {
      const customer = await api.createCustomer(newCustomer);
      setNewCustomer({ ...newCustomer, phone: '' });
      setPage(1);
      if (page === 1) retry();
      setAddMessage('Customer added. You can now manage their vouchers.');
      await viewCustomer(customer);
    } catch (error) {
      setAddError(error.fields?.phone || error.message);
      if (error.code === 'CUSTOMER_EXISTS' && error.customer) {
        setPage(1);
        if (page === 1) retry();
        await viewCustomer(error.customer);
      }
    } finally {
      setAddBusy(false);
    }
  }

  function redeemedVoucher(updated) {
    setVoucherState((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.voucher.id === updated.voucher.id ? updated : item,
      ),
    }));
    retry();
  }

  function claimedVoucher(created) {
    setVoucherState((current) => ({
      ...current,
      items: [created, ...current.items],
    }));
    retry();
  }

  return (
    <>
      <PageHeader
        title="Customers"
        description="View and manage your customer records."
      />
      <section className="panel manual-customer">
        <div>
          <h2>Add customer</h2>
          <p>Enter their mobile number.</p>
        </div>
        <form onSubmit={addCustomer}>
          <Field label="Mobile number" error={addError}>
            <div className={`phone-field ${addError ? 'has-error' : ''}`}>
              <select
                value={newCustomer.country}
                aria-label="Country"
                onChange={(event) => {
                  setNewCustomer({ ...newCustomer, country: event.target.value });
                  setAddError('');
                  setAddMessage('');
                }}
              >
                <option value="SG">🇸🇬 +65</option>
                <option value="MY">🇲🇾 +60</option>
              </select>
              <input
                value={newCustomer.phone}
                inputMode="tel"
                autoComplete="tel"
                placeholder={newCustomer.country === 'SG' ? '9123 4567' : '12 345 6789'}
                aria-invalid={Boolean(addError)}
                onChange={(event) => {
                  setNewCustomer({ ...newCustomer, phone: event.target.value });
                  setAddError('');
                  setAddMessage('');
                }}
                required
              />
            </div>
          </Field>
          <button className="button primary" disabled={addBusy}>
            {addBusy ? 'Adding…' : 'Add customer'}
          </button>
          {addMessage && <small className="manual-customer-success" role="status">{addMessage}</small>}
        </form>
      </section>
      <div className="list-tools">
        <input
          aria-label="Search customers"
          placeholder="Search by first name or phone number"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>
      <section className="customer-layout">
        <div className="panel">
          <Async state={state} retry={retry}>
            {(data) => (
              <>
                <CustomerRows
                  items={data.items}
                  selectedID={selectedCustomer?.id}
                  onSelect={viewCustomer}
                />
                <div className="pagination">
                  <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  <span>
                    Page {data.page} · {data.total} total
                  </span>
                  <button
                    disabled={page * data.pageSize >= data.total}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </Async>
        </div>
        <aside className="panel customer-history">
          <CustomerVoucherHistory
            customer={selectedCustomer}
            state={voucherState}
            activeVoucher={activeVoucher}
            onRedeemed={redeemedVoucher}
            onClaimed={claimedVoucher}
          />
        </aside>
      </section>
    </>
  );
}

export function BusinessSettingsPage({ user, onUserChange }) {
  const business = user.merchant || user.business;
  const [form, setForm] = useState({
    name: business.name,
    outletName: business.outletName,
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(false);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setMessage('');
    try {
      const merchant = await api.updateBusiness(form);
      onUserChange({ ...user, merchant });
      setMessage('Business information saved.');
      setEditing(false);
    } catch (error) {
      setErrors(error.fields || {});
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Business Settings"
        description="Manage your business details shown to customers."
      />
      <form className="panel business-settings" onSubmit={save}>
        <div className="panel-head business-settings-head">
          <div>
            <h2>Business information</h2>
            <p>{editing ? 'Update the details below, then save your changes.' : 'These details appear across your customer-facing pages.'}</p>
          </div>
          {!editing && <button className="button primary" type="button" onClick={() => setEditing(true)}>Edit business information</button>}
        </div>
        <section className="business-photo-setting">
          <div className={`business-photo-preview ${business.logoUrl ? 'has-photo' : ''}`}>
            {business.logoUrl ? (
              <img src={business.logoUrl} alt="Business" />
            ) : (
              <span>{form.name.trim().charAt(0).toUpperCase() || 'L'}</span>
            )}
          </div>
          <div>
            <h2>Business photo</h2>
            <p>Contact us if you would like to add or change your business photo.</p>
            <Link className="button secondary compact" to="/merchant/contact">
              Contact us
            </Link>
          </div>
        </section>
        <div className="settings-fields">
          <Field label="Business name" error={errors.name}>
            <input
              value={form.name}
              disabled={!editing}
              maxLength="120"
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
          </Field>
          <Field label="Outlet name" error={errors.outletName}>
            <input
              value={form.outletName}
              disabled={!editing}
              maxLength="120"
              onChange={(event) => setForm({ ...form, outletName: event.target.value })}
              required
            />
          </Field>
        </div>
        {message && (
          <div className={`alert ${Object.keys(errors).length ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
        {editing && <div className="button-row settings-actions">
          <button className="button primary" disabled={busy}>{busy ? 'Saving…' : 'Save business information'}</button>
          <button className="button secondary" type="button" disabled={busy} onClick={() => {
            setForm({ name: business.name, outletName: business.outletName });
            setErrors({});
            setMessage('');
            setEditing(false);
          }}>Cancel</button>
        </div>}
      </form>
    </>
  );
}

export function ContactPage({ user }) {
  const business = user.merchant || user.business;
  const generalSubject = encodeURIComponent(`LoyalQR support — ${business.name}`);
  const photoSubject = encodeURIComponent(`Business photo change — ${business.name}`);
  const supportReference = business.businessUuid || 'Not available';

  return (
    <>
      <PageHeader
        title="Contact Us"
        description="Get help with your account or business profile."
      />
      <section className="contact-support">
        <div>
          <h2>How can we help?</h2>
          <p>Email our team and quote the business ID below so we can find your account quickly.</p>
          <div className="support-reference"><span>Business ID</span><code>{supportReference}</code></div>
          <a className="button primary" href={`mailto:loyalqr.sg@gmail.com?subject=${generalSubject}`}>
            Email LoyalQR.sg@gmail.com
          </a>
        </div>
        <div className="photo-change-help">
          <h3>Change your business photo</h3>
          <p>Attach the new square logo or storefront photo you want us to use.</p>
          <a href={`mailto:loyalqr.sg@gmail.com?subject=${photoSubject}`}>Request a photo change</a>
        </div>
      </section>
    </>
  );
}

export function QRPage() {
  const [state, retry] = useLoad(api.qr);
  const canvas = useRef();
  const [form, setForm] = useState({ headline: '', subtitle: '' });
  const [savedForm, setSavedForm] = useState({ headline: '', subtitle: '' });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (state.data) {
      const nextForm = { headline: state.data.headline || '', subtitle: state.data.subtitle || '' };
      setForm(nextForm);
      setSavedForm(nextForm);
    }
  }, [state.data]);

  useEffect(() => {
    if (state.data && canvas.current) {
      QRCode.toCanvas(canvas.current, state.data.publicUrl, {
        width: 300,
        margin: 3,
        color: { dark: '#171717', light: '#ffffff' },
      });
    }
  }, [state.data]);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setMessage('');
    try {
      const updated = await api.saveQR(form);
      const nextForm = { headline: updated.headline, subtitle: updated.subtitle };
      setForm(nextForm);
      setSavedForm(nextForm);
      setMessage('Counter sign updated.');
      setEditing(false);
    } catch (error) {
      setErrors(error.fields || {});
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Async state={state} retry={retry}>
      {(data) => (
        <>
          <PageHeader
            title="Your Counter QR"
            description="Customise and print your counter QR sign."
            action={
              <button className="button primary no-print print-sign-button" onClick={() => window.print()}>
                Print QR sign
              </button>
            }
          />
          <section className="qr-workspace">
            <form className="panel qr-controls" onSubmit={save}>
              <div className="panel-head">
                <div>
                  <h2>Counter sign message</h2>
                  <p>{editing ? 'Use a short welcome or scanning instruction.' : 'This message appears above the QR code.'}</p>
                </div>
                {!editing && <button className="button secondary compact" type="button" onClick={() => setEditing(true)}>Edit</button>}
              </div>
              <Field label="Headline" error={errors.headline}>
                <input
                  value={form.headline}
                  maxLength="100"
                  placeholder="Scan to continue"
                  disabled={!editing}
                  onChange={(event) => setForm({ ...form, headline: event.target.value })}
                  required
                />
              </Field>
              <Field label="Subtitle" error={errors.subtitle}>
                <textarea
                  value={form.subtitle}
                  maxLength="180"
                  rows="3"
                  placeholder="Open your camera and point it at the QR code."
                  disabled={!editing}
                  onChange={(event) => setForm({ ...form, subtitle: event.target.value })}
                />
              </Field>
              {message && (
                <div className={`alert ${Object.keys(errors).length ? 'error' : 'success'}`}>
                  {message}
                </div>
              )}
              {editing && <div className="button-row">
                <button className="button primary" disabled={busy}>{busy ? 'Saving…' : 'Save counter sign'}</button>
                <button className="button secondary" type="button" disabled={busy} onClick={() => {
                  setForm(savedForm);
                  setErrors({});
                  setMessage('');
                  setEditing(false);
                }}>Cancel</button>
              </div>}
              <div className="qr-link-block">
                <span>Customer link</span>
                <code>{data.publicUrl}</code>
              </div>
              <p>Customers who cannot scan can give their number to staff instead.</p>
            </form>
            <article className="counter-sign">
              <div className="counter-sign-business">
                {data.business.logoUrl ? (
                  <img src={data.business.logoUrl} alt="" />
                ) : (
                  <span>{data.business.name.trim().charAt(0).toUpperCase()}</span>
                )}
                <strong>{data.business.name}</strong>
              </div>
              <h1>{form.headline || data.headline}</h1>
              {form.subtitle && <p>{form.subtitle}</p>}
              <canvas ref={canvas} />
              <div className="counter-sign-url">
                <span>Or visit</span>
                <a href={data.publicUrl}>{data.publicUrl.replace(/^https?:\/\//, '')}</a>
              </div>
              <footer>
                Powered by <b>LoyalQR.sg</b>.
              </footer>
            </article>
          </section>
        </>
      )}
    </Async>
  );
}
